import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { RiMore2Fill, RiKeyLine, RiLinkM } from '@remixicon/react';
import { cn } from '@/lib/utils';

export interface TableField {
  name: string;
  type: string;
  isPrimary?: boolean;
  isForeign?: boolean;
  nullable?: boolean;
  foreignTable?: string;
}

interface TableNodeData extends Record<string, unknown> {
  label: string;
  fields: TableField[];
  selected?: boolean;
}

type TableNodeType = Node<TableNodeData, 'tableNode'>;

function TableNode({ data, id, selected }: NodeProps<TableNodeType>) {
  return (
    <div
      className={cn(
        'rounded-xl bg-card shadow-lg w-[280px] font-mono border-2',
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-border/50'
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/80 bg-muted/30">
        <div className="text-sm flex items-center gap-2">
          <RiLinkM className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">{data.label}</span>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 hover:bg-background"
          aria-label="Table options"
        >
          <RiMore2Fill className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="text-xs">
        {data.fields.map((field: TableField) => {
          const hasSourceHandle = field.isPrimary;
          const hasTargetHandle = field.isForeign;

          return (
            <div
              key={field.name}
              className="px-4 py-2.5 border-b border-border/30 last:border-0 relative hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {(field.isPrimary || field.isForeign) && (
                    <RiKeyLine
                      className={cn(
                        'h-3 w-3 flex-shrink-0',
                        field.isPrimary ? 'text-amber-500' : 'text-blue-500'
                      )}
                    />
                  )}
                  <span className="truncate font-medium">{field.name}</span>
                </div>
                <span className="text-muted-foreground text-[11px] flex-shrink-0">
                  {field.type}
                  {field.nullable && '?'}
                </span>
              </div>

              {hasSourceHandle && (
                <Handle
                  type="source"
                  position={Position.Right}
                  id={field.name}
                  className="!w-3 !h-3 !bg-amber-500 !border-2 !border-background"
                  style={{ right: -6 }}
                />
              )}

              {hasTargetHandle && (
                <Handle
                  type="target"
                  position={Position.Left}
                  id={field.name}
                  className="!w-3 !h-3 !bg-blue-500 !border-2 !border-background"
                  style={{ left: -6 }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(TableNode);
